import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { useQueryClient } from "@tanstack/react-query";
import { notify } from "utils/notify";
import { useUpdateDeviceRegion } from "hooks/useDevices";
import { Device } from "types/device.types";
import {
  ConfigField,
  RegionFieldRow,
} from "styles/devices/deviceConfig.styles";

const REGION_OPTIONS = [
  { label: "Australia-NZ", value: 0 as const },
  { label: "Europe", value: 1 as const },
];

function toRegion(value?: number): 0 | 1 {
  return Number(value) === 1 ? 1 : 0;
}

interface RegionAdminFieldProps {
  serialNumber: string;
  linkId?: string;
  currentRegion?: number;
}

export function RegionAdminField({
  serialNumber,
  linkId,
  currentRegion,
}: RegionAdminFieldProps) {
  const queryClient = useQueryClient();
  const updateRegion = useUpdateDeviceRegion();
  const [region, setRegion] = useState<0 | 1>(toRegion(currentRegion));

  useEffect(() => {
    if (currentRegion === 0 || currentRegion === 1) {
      setRegion(toRegion(currentRegion));
    }
  }, [currentRegion]);

  const saveRegion = () => {
    updateRegion.mutate(
      { serialNumber, region },
      {
        onSuccess: (response) => {
          queryClient.setQueryData(
            ["getDevice", serialNumber, linkId ?? null],
            (oldData: Device | undefined) => {
              if (!oldData) return oldData;
              return { ...oldData, region: response.data?.region ?? region };
            },
          );
          notify.success("Device region updated successfully");
        },
        onError: (error) => {
          console.error("Failed to update device region:", error);
          notify.error("Failed to update device region");
        },
      },
    );
  };

  const isUnchanged =
    currentRegion === 0 || currentRegion === 1
      ? region === currentRegion
      : false;

  return (
    <ConfigField $width="18rem">
      <label htmlFor="deviceRegion">Region</label>
      <RegionFieldRow>
        <Dropdown
          id="deviceRegion"
          value={region}
          options={REGION_OPTIONS}
          onChange={(e) => setRegion(e.value)}
          placeholder="Select Region"
          disabled={updateRegion.isPending}
        />
        <Button
          label="Save"
          icon="pi pi-save"
          onClick={saveRegion}
          loading={updateRegion.isPending}
          disabled={updateRegion.isPending || isUnchanged}
        />
      </RegionFieldRow>
      <small>Device region from IoT config (admin only)</small>
    </ConfigField>
  );
}
